import { useCallback } from "react";
import { useRouter } from "next/router";
import {
  useProductMode,
  AGENT_LANDING_PATH,
} from "@/oso-for-agents/hooks/useAgentMode";
import { APERTURE_ENVIRONMENT_NAME } from "@/oso-for-agents/constants";

/**
 * Hook for auto-switching hybrid users to Agents UI when they select an
 * Aperture environment that has no API tokens (indicating a fresh welcome flow).
 *
 * Returns a function that checks conditions and triggers the switch if appropriate.
 */
export function useApertureAutoSwitch() {
  const router = useRouter();
  const { product, setProduct, showBothAppsAndAgentsProduct } =
    useProductMode();

  /**
   * Check if we should auto-switch to Agents UI and do so if conditions are met.
   *
   * Conditions for auto-switch:
   * 1. Switching to an Aperture environment
   * 2. User is a hybrid user (showBothAppsAndAgentsProduct)
   * 3. User is currently on Apps UI
   * 4. The Aperture environment has no API tokens (fresh welcome flow)
   */
  const maybeAutoSwitchToAgents = useCallback(
    async (envName: string, envPubId: string) => {
      // Only check if all preconditions are met
      if (
        envName !== APERTURE_ENVIRONMENT_NAME ||
        !showBothAppsAndAgentsProduct ||
        product !== "apps"
      ) {
        return;
      }

      try {
        const response = await fetch(`/web/environment/${envPubId}/tokens`);
        if (response.ok) {
          const tokens = await response.json();
          // If no tokens exist, auto-switch to Agents UI
          if (!tokens || tokens.length === 0) {
            setProduct("agents");
            router.push(AGENT_LANDING_PATH);
          }
        }
      } catch (error) {
        // Fail gracefully - don't auto-switch if we can't check tokens
        console.warn("Failed to check tokens for auto-switch:", error);
      }
    },
    [product, showBothAppsAndAgentsProduct, setProduct, router]
  );

  return { maybeAutoSwitchToAgents };
}
